import { type ComponentType, type ReactNode, useEffect, useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { Link } from 'react-router-dom';
import {
  Accessibility,
  Plus,
  Ruler,
  Scale,
  Shirt,
  Trash2,
  UserRound,
} from 'lucide-react';
import type {
  PatientBaseProfile,
  PatientMeasurementField,
  PatientMeasurementRecord,
} from '../types';
import {
  formatAgeValue,
  formatBiologicalSex,
  formatBirthYearValue,
  formatMeasurementValue,
} from '../utils/formatters';
import { USER_ROUTES } from '../../../routes/userRoutes';
import { buildPatientMeasurementFieldErrors } from '../utils/measurementValidation';
import {
  createExtraMeasurementField,
  sanitizeMeasurementFields,
} from '../utils/measurements';
import { deriveAgeFromDateOfBirth } from '../utils/nutrition';
import {
  birthYearToDateOfBirth,
  getMaxBirthYear,
  isRegisteredBiologicalSex,
  isRegisteredDateOfBirth,
  isRegisteredHeight,
  MIN_BIRTH_YEAR,
  parseBirthYear,
} from '../utils/profile';

interface PatientMeasurementsFormProps {
  patientId: number;
  initialValues: PatientMeasurementRecord;
  loading?: boolean;
  saving?: boolean;
  baseProfile?: PatientBaseProfile | null;
  requireMissingBaseProfile?: boolean;
  onSubmit: (values: PatientMeasurementRecord) => Promise<void> | void;
  onCancel?: () => void;
}

interface NewFieldModalValues {
  label: string;
  unit: string;
}

const createNewFieldModalValues = (): NewFieldModalValues => ({
  label: '',
  unit: '',
});

const BASE_FIELD_ICON_BY_LABEL = {
  Peso: Scale,
  Spalle: Accessibility,
  Fianchi: Ruler,
  Vita: Ruler,
  Petto: Shirt,
  Collo: UserRound,
  Coscia: Ruler,
  Polpacci: Ruler,
} as const;

function MeasurementSectionHeader({
  title,
  description,
  icon,
  action,
}: {
  title: string;
  description: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number }>;
  action?: ReactNode;
}) {
  const Icon = icon;

  return (
    <Group justify='space-between' align='flex-start' gap='sm'>
      <Group align='flex-start' gap='sm' wrap='nowrap'>
        <ThemeIcon size='lg' radius='md' variant='light' color='blue'>
          <Icon size={18} strokeWidth={1.8} />
        </ThemeIcon>
        <Stack gap={4}>
          <Text fw={700}>{title}</Text>
          <Text size='sm' c='dimmed'>
            {description}
          </Text>
        </Stack>
      </Group>
      {action}
    </Group>
  );
}

export function PatientMeasurementsForm({
  patientId,
  initialValues,
  loading = false,
  saving = false,
  baseProfile = null,
  requireMissingBaseProfile = false,
  onSubmit,
  onCancel,
}: PatientMeasurementsFormProps) {
  const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
  const sexLocked = isRegisteredBiologicalSex(baseProfile?.biologicalSex);
  const dateOfBirthLocked = isRegisteredDateOfBirth(baseProfile?.dateOfBirth);
  const heightLocked = isRegisteredHeight(baseProfile?.heightCm);
  const form = useForm<PatientMeasurementRecord>({
    initialValues,
    validateInputOnBlur: true,
    validate: values => {
      const errors: Record<string, string | null> = {
        ...buildPatientMeasurementFieldErrors(values.fields),
      };

      if (requireMissingBaseProfile) {
        if (!sexLocked && !isRegisteredBiologicalSex(values.biologicalSex)) {
          errors.biologicalSex = 'Campo obbligatorio';
        }
        if (
          !dateOfBirthLocked &&
          !isRegisteredDateOfBirth(values.dateOfBirth)
        ) {
          errors.dateOfBirth = 'Campo obbligatorio';
        }
        if (!heightLocked && !isRegisteredHeight(values.heightCm)) {
          errors.heightCm = 'Campo obbligatorio';
        }
      }

      return Object.fromEntries(
        Object.entries(errors).filter(([, message]) => message != null)
      );
    },
  });
  const newFieldForm = useForm<NewFieldModalValues>({
    initialValues: createNewFieldModalValues(),
    validate: {
      label: value =>
        value.trim() === '' ? 'Inserisci il nome del campo extra' : null,
    },
  });

  useEffect(() => {
    form.setValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const openNewFieldModal = () => {
    newFieldForm.setValues(createNewFieldModalValues());
    setNewFieldModalOpen(true);
  };

  const closeNewFieldModal = () => {
    setNewFieldModalOpen(false);
  };

  const addExtraField = (values: NewFieldModalValues) => {
    form.insertListItem(
      'fields',
      createExtraMeasurementField(form.values.fields.length, values)
    );
    closeNewFieldModal();
  };

  const removeField = (index: number) => {
    form.removeListItem('fields', index);
  };

  const baseFields = form.values.fields.filter(
    field => field.scope !== 'extra'
  );
  const extraFields = form.values.fields.filter(
    field => field.scope === 'extra'
  );

  const fieldIndexById = (id: PatientMeasurementField['id']) =>
    form.values.fields.findIndex(field => field.id === id);

  const renderFieldRow = (
    field: PatientMeasurementField,
    index: number,
    variant: 'base' | 'extra'
  ) => {
    const isExtraField = field.scope === 'extra';
    const BaseIcon =
      variant === 'base'
        ? (BASE_FIELD_ICON_BY_LABEL[
            field.label as keyof typeof BASE_FIELD_ICON_BY_LABEL
          ] ?? Accessibility)
        : Plus;

    return (
      <Grid key={String(field.id ?? index)} align='center' gutter='md'>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Group align='center' gap='sm' wrap='nowrap'>
            <ThemeIcon size='lg' radius='xl' variant='light' color='blue'>
              <BaseIcon size={18} strokeWidth={1.8} />
            </ThemeIcon>
            <Text fw={700}>{field.label}</Text>
          </Group>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 3 }}>
          <Stack gap={4}>
            <Text size='sm' c='dimmed'>
              {field.unit?.trim() ? `Unità: ${field.unit}` : 'Unità libera'}
            </Text>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 5 }}>
          <Group align='flex-end' gap='xs' wrap='nowrap'>
            <TextInput
              aria-label={`Valore ${field.label}`}
              placeholder='Es. 68.4'
              radius='md'
              required
              disabled={loading || saving}
              flex={1}
              {...form.getInputProps(`fields.${index}.value`)}
            />
            {isExtraField && (
              <ActionIcon
                variant='light'
                color='red'
                onClick={() => removeField(index)}
                disabled={loading || saving}
                aria-label={`Rimuovi campo ${field.label}`}
                radius='md'
              >
                <Trash2 size={18} />
              </ActionIcon>
            )}
          </Group>
        </Grid.Col>
      </Grid>
    );
  };

  return (
    <>
      <Modal
        opened={newFieldModalOpen}
        onClose={closeNewFieldModal}
        title='Nuovo campo extra'
        centered
        withinPortal={false}
        keepMounted
        transitionProps={{ duration: 0 }}
      >
        <form onSubmit={newFieldForm.onSubmit(addExtraField)}>
          <Stack gap='sm'>
            <Text size='sm' c='dimmed'>
              Definisci il nuovo campo extra. Il valore lo compilerai poi
              direttamente nella rilevazione.
            </Text>
            <TextInput
              label='Etichetta campo'
              placeholder='Es. Fianchi'
              {...newFieldForm.getInputProps('label')}
            />
            <TextInput
              label='Unità'
              placeholder='cm, kg, %...'
              {...newFieldForm.getInputProps('unit')}
            />
            <Group justify='flex-end'>
              <Button
                type='button'
                variant='default'
                onClick={closeNewFieldModal}
              >
                Annulla
              </Button>
              <Button type='submit'>Conferma nuovo campo</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Box
        component='form'
        onSubmit={form.onSubmit(values =>
          onSubmit({
            ...values,
            patientId,
            notes: values.notes.trim(),
            fields: sanitizeMeasurementFields(values.fields),
            biologicalSex: sexLocked
              ? (baseProfile?.biologicalSex ?? null)
              : (values.biologicalSex ?? null),
            heightCm: heightLocked
              ? (baseProfile?.heightCm ?? null)
              : isRegisteredHeight(values.heightCm)
                ? values.heightCm
                : null,
            dateOfBirth: dateOfBirthLocked
              ? (baseProfile?.dateOfBirth ?? null)
              : isRegisteredDateOfBirth(values.dateOfBirth)
                ? values.dateOfBirth.trim()
                : null,
          })
        )}
      >
        <Stack gap='lg'>
          {requireMissingBaseProfile && (
            <Card withBorder radius='sm' p='lg'>
              <Stack gap='md'>
                <Group justify='space-between' align='flex-start' wrap='wrap'>
                  <Stack gap={4}>
                    <Text fw={700}>Dati utente</Text>
                    <Text size='sm' c='dimmed'>
                      Sesso, anno di nascita e altezza già registrati non si
                      modificano da qui. Per cambiarli vai alla pagina utente.
                    </Text>
                  </Stack>
                  <Anchor
                    component={Link}
                    to={USER_ROUTES.detail(patientId)}
                    size='sm'
                  >
                    Apri pagina utente
                  </Anchor>
                </Group>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    {sexLocked ? (
                      <Stack gap={4}>
                        <Text size='sm' c='dimmed'>
                          Sesso biologico
                        </Text>
                        <Text fw={600}>
                          {formatBiologicalSex(
                            baseProfile?.biologicalSex ?? null
                          )}
                        </Text>
                      </Stack>
                    ) : (
                      <Select
                        label='Sesso biologico'
                        placeholder='Seleziona'
                        required
                        data={[
                          { value: 'male', label: 'Maschio' },
                          { value: 'female', label: 'Femmina' },
                        ]}
                        disabled={loading || saving}
                        {...form.getInputProps('biologicalSex')}
                      />
                    )}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    {dateOfBirthLocked ? (
                      <Stack gap={4}>
                        <Text size='sm' c='dimmed'>
                          Anno di nascita
                        </Text>
                        <Text fw={600}>
                          {formatBirthYearValue(baseProfile?.dateOfBirth)}
                        </Text>
                        <Text size='sm' c='dimmed'>
                          {formatAgeValue(
                            deriveAgeFromDateOfBirth(
                              baseProfile?.dateOfBirth ?? null
                            )
                          )}
                        </Text>
                      </Stack>
                    ) : (
                      <NumberInput
                        label='Anno di nascita'
                        description='Solo anno: serve per stimare l’età'
                        placeholder='Es. 1991'
                        required
                        min={MIN_BIRTH_YEAR}
                        max={getMaxBirthYear()}
                        allowDecimal={false}
                        disabled={loading || saving}
                        value={parseBirthYear(form.values.dateOfBirth) ?? ''}
                        error={form.errors.dateOfBirth}
                        onChange={value => {
                          const year =
                            typeof value === 'number'
                              ? value
                              : Number.parseInt(String(value), 10);
                          if (!Number.isInteger(year)) {
                            form.setFieldValue('dateOfBirth', '');
                            return;
                          }
                          form.setFieldValue(
                            'dateOfBirth',
                            birthYearToDateOfBirth(year)
                          );
                        }}
                      />
                    )}
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    {heightLocked ? (
                      <Stack gap={4}>
                        <Text size='sm' c='dimmed'>
                          Altezza
                        </Text>
                        <Text fw={600}>
                          {formatMeasurementValue(
                            baseProfile?.heightCm ?? null,
                            'cm'
                          )}
                        </Text>
                      </Stack>
                    ) : (
                      <NumberInput
                        label='Altezza (cm)'
                        placeholder='Es. 172'
                        required
                        min={1}
                        decimalScale={1}
                        disabled={loading || saving}
                        {...form.getInputProps('heightCm')}
                      />
                    )}
                  </Grid.Col>
                </Grid>
              </Stack>
            </Card>
          )}

          <Card withBorder radius='sm' p='lg'>
            <Stack gap='md'>
              <MeasurementSectionHeader
                title='Campi base rilevazione'
                description='Tutti i valori base sono obbligatori: la rilevazione non può contenere campi vuoti.'
                icon={Accessibility}
              />

              <Grid gutter='md' visibleFrom='md'>
                <Grid.Col span={4}>
                  <Text size='sm' fw={600} c='dimmed'>
                    Misurazione
                  </Text>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Text size='sm' fw={600} c='dimmed'>
                    Dettagli
                  </Text>
                </Grid.Col>
                <Grid.Col span={5}>
                  <Text size='sm' fw={600} c='dimmed'>
                    Valore
                  </Text>
                </Grid.Col>
              </Grid>

              <Stack gap='md'>
                {baseFields.map((field, rowIndex) => {
                  const index = fieldIndexById(field.id);
                  return (
                    <Stack key={String(field.id ?? index)} gap='md'>
                      {rowIndex > 0 && <Divider />}
                      {renderFieldRow(field, index, 'base')}
                    </Stack>
                  );
                })}
              </Stack>

              <Divider />

              <MeasurementSectionHeader
                title='Campi extra'
                description='Aggiungi campi personalizzati che non fanno parte del set base.'
                icon={Plus}
                action={
                  <Button
                    type='button'
                    variant='outline'
                    leftSection={<Plus size={16} />}
                    onClick={openNewFieldModal}
                    disabled={loading || saving}
                  >
                    Aggiungi campo
                  </Button>
                }
              />

              {extraFields.length === 0 ? (
                <Text size='sm' c='dimmed'>
                  Nessun campo extra ancora definito. Usa la modale per
                  aggiungerne uno nuovo.
                </Text>
              ) : (
                <Stack gap='md'>
                  <Grid gutter='md' visibleFrom='md'>
                    <Grid.Col span={4}>
                      <Text size='sm' fw={600} c='dimmed'>
                        Misurazione
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Text size='sm' fw={600} c='dimmed'>
                        Dettagli
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={5}>
                      <Text size='sm' fw={600} c='dimmed'>
                        Valore
                      </Text>
                    </Grid.Col>
                  </Grid>

                  {extraFields.map((field, rowIndex) => {
                    const index = fieldIndexById(field.id);
                    return (
                      <Stack key={String(field.id ?? index)} gap='md'>
                        {rowIndex > 0 && <Divider />}
                        {renderFieldRow(field, index, 'extra')}
                      </Stack>
                    );
                  })}
                </Stack>
              )}

              <Divider />

              <Stack gap='sm'>
                <Text fw={700}>Note rilevazione</Text>
                <Textarea
                  placeholder='Osservazioni cliniche, contesto della rilevazione, note operative...'
                  minRows={4}
                  disabled={loading || saving}
                  {...form.getInputProps('notes')}
                />
              </Stack>
            </Stack>
          </Card>

          <Group justify='flex-end' gap='sm'>
            {onCancel && (
              <Button variant='default' onClick={onCancel} disabled={saving}>
                Chiudi
              </Button>
            )}
            <Button type='submit' loading={saving}>
              Registra rilevazione
            </Button>
          </Group>
        </Stack>
      </Box>
    </>
  );
}
